import Button from '@alfa-retail-react/components/button';
import Modal from '@alfa-retail-react/components/modal';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
    useNavigate,
    PageFrame,
    CollapseSection,
    Divider,
    MainScreen,
    OverContentLoadableBlock,
} from '@alfa-retail-react/components';
import {
    LoginHint,
    UrlUtils,
    MsiResponsePayload,
    useAuthentication,
} from 'msisdk-react';

import { authorizeUrlService } from 'app/logic/pages/redirect/AuthorizeUrlService';
import {
    STEP_PROGRESS_PERCENT,
    StepOrder,
    TOTAL_STEP_COUNT,
} from 'app/constants/step';
import { Step } from 'app/component/step/Step';
import { useIsDesktop } from 'app/utils/useWindowType';
import { msiInstructionList } from 'app/logic/pages/redirect/msiInstructionList';
import { OrderedPanelList } from 'app/component/orderedPanelList/OrderedPanelList';
import { ERROR_PAGE_URL } from 'app/logic/pages/error/ErrorPage';
import { RedirectPageStore } from 'app/logic/pages/redirect/RedirectPageStore';
import { networkExceptionHandlerOnLoadData } from 'app/logic/pages/error/networkExceptionHandler';
import { TokenRequest } from 'app/auth/TokenRequest';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { useAnalytics } from 'app/analytics/useAnalytics';

import './RedirectPage.less';

/**
 * Путь к странице с редиректом в МСИ.
 */
export const REDIRECT_PAGE_URL = '/redirect';

export interface RedirectPageState {
    /**
     * Url авторизации.
     */
    authorizeUrl: string;

    /**
     * Номер телефона.
     */
    phoneNumber: string;
}

const PAGE_NAME = 'RedirectPage';
const CONSULT_TAKE_ACTION = 'Получить консультацию';
const ACTION_TEXT = 'Перейти в МСИ';
const MESSAGE =
    'Для оформления кредитной карты онлайн вам нужно пройти идентификацию в Межбанковской системе идентификации (МСИ). Это займет не более 2 минут. Оформите заявку когда удобно - мы предзаполним данные за вас, а вы сэкономите время для других важных дел.';
const MESSAGE_LIST_PREFIX = 'Вам понадобятся:';
const MESSAGE_LIST = [
    'идентификационный (личный) номер паспорта или ID-карты;',
    'телефон, на который придёт СМС-пароль;',
    'рабочая камера на вашем устройстве.',
];

const CONSULT_TITLE = 'Получить консультацию?';
const CONSULT_DESCRIPTION =
    'Вам перезвонит сотрудник банка в ближайшее время, ответит на вопросы и поможет оформить заявку на карту по телефону';
const CONSULT_ACTION_TEXT = 'Да, дождусь звонка';
const CONSULT_CANCEL_TEXT = 'Нет, оформлю через МСИ';
const CONSULT_RESULT_MESSAGE =
    'Мы свяжемся с вами в ближайшее время и ответим на все вопросы';
const CONSULT_RESULT_ACTION = 'Закрыть';

/**
 * Вспомогательная страница для предоставления пользователю возможности перехода к аутентификации в МСИ.
 */
export const RedirectPage = observer(() => {
    const [isSectionOpen, setIsSectionOpen] = useState(false);
    const applicationContext = useApplicationContext();
    const { additionalLogo, cardProduct } = applicationContext;
    const { state } = useLocation();
    const navigate = useNavigate();
    const [isDesktop] = useIsDesktop();
    const [open, setOpen] = useState(false);
    const analytics = useAnalytics(cardProduct.code, PAGE_NAME, false);
    const pageStore = useRef(new RedirectPageStore()).current;
    const redirectPageState = state as RedirectPageState;
    const orderId = sessionStorage.getItem('orderId');

    // Отключено автоматическое открытие pop-up т.к. некоторые браузеры блокируют всплывающие окна, что приводит к некорректной работе впоследствии
    // https://projects.alfabank.by/browse/CC2C-952
    // useEffect(() => {
    //     handleOpenMSI();
    // }, [])

    useEffect(() => {
        analytics.pageDisplayed();
    }, []);

    const onSuccess = async (payload: MsiResponsePayload) => {
        const tokenRequest = new TokenRequest(payload.code, payload.state);

        analytics.fullEvent('MsiDataAccessSuccess');

        pageStore
            .getToken(tokenRequest)
            .then(() => {
                analytics.fullEvent('MsiTokenRetrievalSuccess');
                navigate(-1);
            })
            .catch((e) => {
                analytics.fullEvent('MsiTokenRetrievalFail');
                throw e;
            })
            .catch(networkExceptionHandlerOnLoadData({ navigate }));
    };

    const onError = () => {
        analytics.fullEvent('MsiDataAccessRestrictedEventOccurred');

        const AUTH_ERROR_TITLE = 'Вы не предоставили доступ к данным';
        const AUTH_ERROR_MESSAGE: string =
            'Чтобы продолжить оформление кредитной заявки, дайте согласие на получение ваших персональных данных из МСИ';

        navigate(ERROR_PAGE_URL, {
            title: AUTH_ERROR_TITLE,
            messages: [AUTH_ERROR_MESSAGE],
        });
    };

    const { openAuthentication } = useAuthentication({
        onSuccess,
        onError,
    });

    /**
     * Получение url'а авторизации МСИ.
     * Дополнительно добавляет параметры необходимые для авторизации в МСИ.
     *
     * @param rawAuthorizeUrl исходный url авторизации.
     * @return url авторизации с добавленными параметрами.
     */
    const getMSIAuthorizeUrl = (rawAuthorizeUrl: string): string => {
        const params: Map<string, string> = new Map<string, string>().set(
            LoginHint.QUERY_STRING_PARAM,
            new LoginHint()
                .phone(redirectPageState.phoneNumber)
                .queryStringValue(),
        );

        return UrlUtils.setUrlParams(rawAuthorizeUrl, params);
    };

    /**
     * Переход на сервис авторизации МСИ.
     *
     * @param authorizeUrl url авторизации.
     */
    const redirectToAuthorizeMSI = (authorizeUrl: string) => {
        analytics.confirm();
        const msiAuthorizeUrl = getMSIAuthorizeUrl(authorizeUrl);

        authorizeUrlService.setAuthorizeUrl(msiAuthorizeUrl);
        return openAuthentication(msiAuthorizeUrl);
    };

    const toggleModal = useCallback(() => setOpen(!open), [open]);
    const handleSectionClick = useCallback(
        () => setIsSectionOpen(!isSectionOpen),
        [isSectionOpen],
    );

    const handleTakeConsult = async () => {
        await pageStore.cancelOrder(orderId);
        analytics.common('CallActionInitiated');
        navigate('/result', {
            actionButtonTitle: CONSULT_RESULT_ACTION,
            contentMessages: [CONSULT_RESULT_MESSAGE],
            disableAnalytics: true,
        });
    };

    const handleOpenMSI = () =>
        redirectToAuthorizeMSI(redirectPageState?.authorizeUrl);

    const isLoading = pageStore.actionStateStore.isLoading;

    return (
        <OverContentLoadableBlock show={isLoading}>
            <PageFrame
                rightAddonLogo={additionalLogo}
                className={'redirectPage'}
                displayFooter
            >
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle()}
                    />
                    <MainScreen.ContentBar>
                        <Step
                            hideStep
                            hint={
                                <>
                                    Вероятность одобрения карты{' '}
                                    <span className="progress">
                                        +{STEP_PROGRESS_PERCENT}%
                                    </span>
                                </>
                            }
                            current={StepOrder.STEP_VIEW_PRODUCT_DATA}
                            total={TOTAL_STEP_COUNT}
                        />
                        <p className="redirectPageMessage">
                            {MESSAGE}
                            <br />
                            {MESSAGE_LIST_PREFIX}
                            <br />
                        </p>
                        <div className="redirectPageRequirementsList">
                            {MESSAGE_LIST.map((item, index) => (
                                <div
                                    className="redirectPageRequirementsListItem"
                                    key={index}
                                >
                                    <div>{item}</div>
                                </div>
                            ))}
                        </div>
                        <CollapseSection
                            open={isSectionOpen}
                            onClick={handleSectionClick}
                            className={classNames({ 'is-open': isSectionOpen })}
                            header="Что такое МСИ?"
                        >
                            <div className="sectionDescription">
                                <p className="sectionDescriptionParagraph">
                                    Если вы являетесь клиентом любого банка
                                    Беларуси, информация о вас уже содержится в
                                    государственной системе МСИ. МСИ позволяет
                                    безопасно хранить и передавать ваши данные
                                    между всеми банками страны.
                                </p>
                                <p className="sectionDescriptionParagraph">
                                    Для оформления кредитной карты онлайн вам
                                    нужно предоставить данные из МСИ для Альфа
                                    Банка.
                                </p>
                            </div>
                            <OrderedPanelList items={msiInstructionList} />
                        </CollapseSection>
                        <Modal
                            isDesktop={isDesktop}
                            title={CONSULT_TITLE}
                            open={open}
                            onClose={toggleModal}
                            content={CONSULT_DESCRIPTION}
                            actionButton={
                                <div className="consultActions">
                                    <Button
                                        block
                                        view="accent"
                                        size="m"
                                        onClick={handleTakeConsult}
                                        disabled={isLoading}
                                        style={{
                                            marginBottom: isDesktop ? 24 : 16,
                                        }}
                                    >
                                        {CONSULT_ACTION_TEXT}
                                    </Button>
                                    <Button
                                        block
                                        view="secondary"
                                        size="m"
                                        onClick={toggleModal}
                                        disabled={isLoading}
                                    >
                                        {CONSULT_CANCEL_TEXT}
                                    </Button>
                                </div>
                            }
                        />
                        <Divider className="redirectSectionDivider" />
                        <div className="redirectPageActions">
                            <Button
                                view="secondary"
                                size="m"
                                disabled={isLoading}
                                onClick={toggleModal}
                            >
                                {CONSULT_TAKE_ACTION}
                            </Button>
                            <Button
                                view="accent"
                                size="m"
                                disabled={isLoading}
                                onClick={handleOpenMSI}
                            >
                                {ACTION_TEXT}
                            </Button>
                        </div>
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
